import { useCallback, useMemo } from 'react'

import { IconType } from '@factorialco/f0-react'
import { Folder } from '@factorialco/f0-react/icons/app'
import { ExpensablesDataCollectionQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'

type Expensable =
  ExpensablesDataCollectionQuery['expenses']['expensablesConnection']['nodes'][number]

type ItemAction =
  | {
      id: string
      label: string
      onClick: () => void
      type?: 'primary' | 'secondary' | 'other'
      icon?: IconType
    }
  | {
      type: 'separator'
    }

interface UseMyExpensesItemActionsProps {
  expensableGroupsEnabled: boolean
  employeeId: string
  handlers: {
    openAddToGroupModal: (expensables: Expensable[]) => void
    openRemoveFromGroupModal: (expensables: Expensable[]) => void
  }
  navigationHandler: {
    navigate: (to: string) => void
    navigation: ReturnType<typeof useExpensesNavigation>['navigation']
  }
  isViewingGroup?: boolean
}

const canAddToGroup = (expensable: Expensable, employeeId: string): boolean => {
  return expensable.status === 'draft' && !expensable.groupId && expensable.owner?.id === employeeId
}

const canRemoveFromGroup = (expensable: Expensable): boolean => {
  return expensable.status === 'draft' && !!expensable.groupId
}

const canViewGroup = (expensable: Expensable, isViewingGroup: boolean): boolean => {
  return !!expensable.groupId && !isViewingGroup
}

export const useMyExpensesItemActions = ({
  expensableGroupsEnabled,
  employeeId,
  handlers,
  navigationHandler,
  isViewingGroup = false,
}: UseMyExpensesItemActionsProps) => {
  const executeAction = useCallback(
    (actionId: string, expensable: Expensable) => {
      switch (actionId) {
        case 'add-to-group':
          handlers.openAddToGroupModal([expensable])
          break

        case 'remove-from-group':
          handlers.openRemoveFromGroupModal([expensable])
          break

        case 'view-group':
          if (!expensable.groupId) return
          navigationHandler.navigate(
            navigationHandler.navigation.helpers.groupDetail(expensable.groupId)
          )
          break
      }
    },
    [handlers, navigationHandler]
  )

  const getItemActions = useMemo(
    () =>
      (expensable: Expensable): ItemAction[] => {
        if (!expensableGroupsEnabled) {
          return []
        }

        const actions: ItemAction[] = []

        if (canAddToGroup(expensable, employeeId)) {
          actions.push({
            id: 'add-to-group',
            label: i18n.t('expenses_v2.buttons.add_to_group_v2'),
            onClick: () => executeAction('add-to-group', expensable),
          })
        }

        if (canRemoveFromGroup(expensable)) {
          actions.push({
            id: 'remove-from-group',
            label: i18n.t('expenses_v2.buttons.remove_from_group_v2'),
            onClick: () => executeAction('remove-from-group', expensable),
          })
        }

        if (canViewGroup(expensable, isViewingGroup)) {
          if (actions.length > 0) {
            actions.push({ type: 'separator' })
          }

          actions.push({
            id: 'view-group',
            label: i18n.t('expenses_v2.list.actions.view_group'),
            type: 'secondary',
            icon: Folder,
            onClick: () => executeAction('view-group', expensable),
          })
        }

        return actions
      },
    [expensableGroupsEnabled, employeeId, executeAction]
  )

  return {
    getItemActions,
  }
}
